import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateClient } from '../api/queries/clients';
import { useLookups, activeOptionsFor } from '../api/queries/lookups';
import { LookupSelect } from '../components/LookupSelect';
import { ApiError } from '../api/client';
import { PhoneListInput, type PhoneEntry } from '../components/PhoneListInput';
import { stripPhoneForStorage } from '../utils/phone';
import type { ClientStatus } from '@stfors/shared-types';
import './NewClientPage.css';

export function NewClientPage(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateClient();
  const lookups = useLookups();
  const titleOptions = activeOptionsFor(lookups.data?.lookup_options, 'title');
  const suffixOptions = activeOptionsFor(lookups.data?.lookup_options, 'suffix');
  const phoneTypeOptions = activeOptionsFor(lookups.data?.lookup_options, 'phone_type');

  const [kind, setKind] = useState<'individual' | 'company'>('individual');
  const [title, setTitle] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [suffix, setSuffix] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  // Multi-phone — start with one empty Mobile row so the user has
  // something to fill in immediately. They can add more with "+ Add phone".
  const [phones, setPhones] = useState<PhoneEntry[]>([{ phone_type: 'Mobile', phone: '' }]);
  const [industry, setIndustry] = useState('');
  const [status, setStatus] = useState<ClientStatus>('active');
  const [tagsRaw, setTagsRaw] = useState('');
  const [notes, setNotes] = useState('');

  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setFieldErrors({});

    const tags = tagsRaw
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);

    try {
      // Normalize each phone row to 10-digit storage form, drop empties.
      const phonesPayload = phones
        .map((p) => ({
          phone_type: p.phone_type,
          phone: stripPhoneForStorage(p.phone) ?? '',
        }))
        .filter((p) => p.phone.length === 10 && p.phone_type.length > 0);

      await create.mutateAsync({
        kind,
        title: title.trim() || undefined,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        suffix: suffix.trim() || undefined,
        company_name: kind === 'company' ? companyName.trim() || undefined : undefined,
        email: email.trim() || undefined,
        phones: phonesPayload.length > 0 ? phonesPayload : undefined,
        industry: kind === 'company' ? industry.trim() || undefined : undefined,
        notes: notes.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
        status,
      });
      navigate('/clients');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { details?: { fieldErrors?: Record<string, string[]> } } | null;
        const fe = body?.details?.fieldErrors;
        if (fe) {
          const flat: Record<string, string> = {};
          for (const [k, v] of Object.entries(fe)) {
            if (Array.isArray(v) && v.length > 0) flat[k] = v[0]!;
          }
          setFieldErrors(flat);
          setTopError('Please fix the highlighted fields.');
          return;
        }
      }
      if (err instanceof ApiError && err.status === 403) {
        setTopError("You don't have permission to create clients.");
        return;
      }
      setTopError('Could not create the client. Please try again.');
    }
  };

  return (
    <section className="new-client-section">
      <header className="new-client-header">
        <h2>New Client</h2>
        <button
          type="button"
          className="btn-text"
          onClick={() => navigate('/clients')}
        >
          ← Back to clients
        </button>
      </header>

      <form className="new-client-form" onSubmit={onSubmit} noValidate>
        <div className="new-client-row new-client-kind-toggle">
          <button
            type="button"
            className={kind === 'individual' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => setKind('individual')}
          >
            Individual
          </button>
          <button
            type="button"
            className={kind === 'company' ? 'btn-cyan-primary' : 'btn-charcoal-secondary'}
            onClick={() => setKind('company')}
          >
            Company
          </button>
        </div>

        {kind === 'company' && (
          <div className="new-client-row">
            <label className="new-client-field new-client-field-full">
              <span>Company name *</span>
              <input
                type="text"
                className="form-input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                maxLength={200}
                autoComplete="organization"
              />
              {fieldErrors['company_name'] && <span className="field-error">{fieldErrors['company_name']}</span>}
            </label>
          </div>
        )}

        {kind === 'company' && (
          <span className="new-client-section-label">Primary contact (optional)</span>
        )}

        <div className="new-client-row">
          <label className="new-client-field new-client-field-narrow">
            <span>Title</span>
            <select
              className="form-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoComplete="honorific-prefix"
            >
              <option value="">—</option>
              {titleOptions.map((o) => (
                <option key={o.id} value={o.value}>{o.value}</option>
              ))}
            </select>
            {fieldErrors['title'] && <span className="field-error">{fieldErrors['title']}</span>}
          </label>

          <label className="new-client-field">
            <span>First name</span>
            <input
              type="text"
              className="form-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={100}
              autoComplete="given-name"
            />
            {fieldErrors['first_name'] && <span className="field-error">{fieldErrors['first_name']}</span>}
          </label>

          <label className="new-client-field">
            <span>Last name{kind === 'individual' ? ' *' : ''}</span>
            <input
              type="text"
              className="form-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required={kind === 'individual'}
              maxLength={100}
              autoComplete="family-name"
            />
            {fieldErrors['last_name'] && <span className="field-error">{fieldErrors['last_name']}</span>}
          </label>

          <label className="new-client-field new-client-field-narrow">
            <span>Suffix</span>
            <select
              className="form-input"
              value={suffix}
              onChange={(e) => setSuffix(e.target.value)}
              autoComplete="honorific-suffix"
            >
              <option value="">—</option>
              {suffixOptions.map((o) => (
                <option key={o.id} value={o.value}>{o.value}</option>
              ))}
            </select>
            {fieldErrors['suffix'] && <span className="field-error">{fieldErrors['suffix']}</span>}
          </label>
        </div>

        <div className="new-client-row">
          <label className="new-client-field new-client-field-full">
            <span>Email</span>
            <input
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
            {fieldErrors['email'] && <span className="field-error">{fieldErrors['email']}</span>}
          </label>
        </div>

        <div className="new-client-field new-client-field-full">
          <span className="new-client-section-label">Phone numbers</span>
          <PhoneListInput
            value={phones}
            onChange={setPhones}
            typeOptions={phoneTypeOptions}
            disabled={create.isPending}
          />
          {fieldErrors['phones'] && <span className="field-error">{fieldErrors['phones']}</span>}
        </div>

        <div className="new-client-row">
          {kind === 'company' && (
            <label className="new-client-field">
              <span>Industry</span>
              <input
                type="text"
                className="form-input"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                maxLength={100}
              />
            </label>
          )}

          <label className="new-client-field">
            <span>Status</span>
            <LookupSelect
              category="client_status"
              value={status}
              onChange={setStatus}
              className="form-input"
              aria-label="Status"
            />
          </label>
        </div>

        <label className="new-client-field new-client-field-full">
          <span>Tags (comma separated)</span>
          <input
            type="text"
            className="form-input"
            value={tagsRaw}
            onChange={(e) => setTagsRaw(e.target.value)}
            placeholder="e.g. vip, retainer, criminal-defense"
          />
        </label>

        <label className="new-client-field new-client-field-full">
          <span>Notes</span>
          <textarea
            className="form-input new-client-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            maxLength={10000}
          />
        </label>

        {topError && (
          <p className="new-client-error" role="alert">
            {topError}
          </p>
        )}

        <div className="new-client-actions">
          <button
            type="button"
            className="btn-charcoal-secondary"
            onClick={() => navigate('/clients')}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={create.isPending || (kind === 'individual' ? lastName.trim().length === 0 : companyName.trim().length === 0)}
          >
            {create.isPending ? 'Creating…' : 'Create client'}
          </button>
        </div>
      </form>
    </section>
  );
}
